import { useCallback, useEffect, useState } from "react"

import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { Button, Divider, Icon, Label, Segment, SegmentGroup } from "semantic-ui-react"
import type { OpenAPI } from "simplydo/interfaces"
import { useThrottle } from "simplydo/react/hooks"

import api from "api"
import util from "utils/utils"

import { ImpactStatementEditor } from "../../ImpactStatements/ImpactStatementEditor"

type ImpactStatements = OpenAPI.GET<"/ideas/{idea_id}/impactStatement">["response"]["impactStatements"]

type ImpactStatementManagementProps = {
  idea: OpenAPI.GET<"/ideas/{id}">["response"]
  onUpdateIdea: (data: any) => void
  impactList: any[]
}

const ImpactStatementManagement = ({ idea, onUpdateIdea, impactList }: ImpactStatementManagementProps) => {
  const { t } = useTranslation()

  const [currentImpactStage, setCurrentImpactStage] = useState<ImpactStatements["userEstimate"]["stage"] | null>(null)
  const [impactStatements, setImpactStatements] = useState<ImpactStatements>({})

  const throttledUpdate = useThrottle(onUpdateIdea, 400, [onUpdateIdea])
  const updateImpactStatements = useCallback(
    (impactStatements: ImpactStatements) => {
      setImpactStatements(impactStatements)

      const mostImportantStatement =
        impactStatements.finalOutcome || impactStatements.projectEstimate || impactStatements.userEstimate
      throttledUpdate({
        totalImpacts: Object.entries(mostImportantStatement.impacts)
          .filter(([_key, value]) => value.enabled)
          .map(([key, value]) => {
            const impactOptions = idea?.ownerChallenge?.impactStatements?.impacts?.[key]?.options || []

            let maxWeight = 1
            for (const option of impactOptions) {
              if (option.weight == null) continue
              if (option.weight > maxWeight) {
                maxWeight = option.weight
              }
            }

            return {
              impactId: key,
              ownerImpact: impactList.find((impact) => impact._id === key),
              ...value,
              maxWeight,
            }
          }),
      })
    },
    [throttledUpdate, impactList, idea?.ownerChallenge?.impactStatements],
  )

  const deleteStatement = (impactStatementId: string) => {
    api.ideas.deleteImpactStatement(
      idea._id,
      impactStatementId,
      () => {
        toast.success("Statement deleted successfully")
        setImpactStatements((prev) => {
          const newStatements = { ...prev }
          delete newStatements[impactStatementId]
          return newStatements
        })
      },
      () => {
        toast.error("Failed to delete statement")
      },
    )
  }

  useEffect(() => {
    api.ideas.getImpactStatements(
      idea._id,
      null,
      ({ impactStatements }) => {
        setImpactStatements(impactStatements)
      },
      () => {},
    )
  }, [idea._id])

  if (currentImpactStage) {
    return (
      <div>
        <Button basic compact onClick={() => setCurrentImpactStage(null)}>
          <Icon name="arrow left" />
          Back to overview
        </Button>

        <Divider />

        <ImpactStatementEditor
          impactStatements={impactStatements}
          forStage={currentImpactStage}
          setImpactStatements={updateImpactStatements}
          statementDefintion={idea.ownerChallenge.impactStatements}
          ideaId={idea._id}
          restrictStagesTo={[currentImpactStage]}
          editable={currentImpactStage !== "userEstimate"}
        />

        <Divider hidden />
        <Button
          secondary
          onClick={() => {
            setCurrentImpactStage(null)
          }}
        >
          <Icon name="check" />
          Done
        </Button>
      </div>
    )
  }

  return (
    <div>
      <SegmentGroup>
        {idea.ownerChallenge.impactStatements.showOnIdea ? (
          <Segment secondary={!impactStatements.userEstimate}>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div>
                <h3>
                  <Icon name="wpforms" /> User estimate
                </h3>
                This estimate is based on the estimation of the user who created the {t("generic.idea")}.
              </div>
              {impactStatements.userEstimate ? (
                <>
                  <div>
                    <Label>
                      {util.pluralise(Object.keys(impactStatements.userEstimate.impacts).length, "impact", "impacts")}
                    </Label>
                  </div>
                  <div style={{ display: "flex", flex: 1 }}>
                    <Button
                      style={{ flex: 1 }}
                      onClick={() => {
                        setCurrentImpactStage("userEstimate")
                      }}
                    >
                      View details
                    </Button>
                  </div>
                </>
              ) : null}
            </div>
          </Segment>
        ) : null}
        <Segment secondary={!impactStatements.projectEstimate}>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div>
              <h3>
                <Icon name="object group outline" />
                Project estimate
              </h3>
              This estimate is based on the estimation as part of the {t("generic.idea")}'s assessment. It is only
              visible to project assignees and {t("generic.challenge")} managers.
            </div>
            {!impactStatements.projectEstimate ? (
              <Button
                secondary
                onClick={() => {
                  setCurrentImpactStage("projectEstimate")
                }}
              >
                <Icon name="plus" />
                Create estimate
              </Button>
            ) : (
              <>
                <div>
                  <Label>{Object.keys(impactStatements.projectEstimate.impacts).length} impacts</Label>
                </div>
                <div style={{ display: "flex", flex: 1 }}>
                  <Button
                    style={{ flex: 1 }}
                    onClick={() => {
                      setCurrentImpactStage("projectEstimate")
                    }}
                  >
                    <Icon name="pencil" />
                    Edit
                  </Button>
                  <Button
                    icon
                    basic
                    onClick={() => {
                      util
                        .confirm(
                          "Delete the project estimate?",
                          "Are you sure you want to delete the project estimate statement? This actions is instant and irreversible.",
                        )
                        .then(() => {
                          deleteStatement(impactStatements.projectEstimate._id)
                        })
                        .catch(() => {})
                    }}
                  >
                    <Icon name="trash" />
                  </Button>
                </div>
              </>
            )}
          </div>
        </Segment>
        <Segment secondary={!impactStatements.finalOutcome}>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div>
              <h3>
                <Icon name="line graph" />
                Final outcome
              </h3>
              This statement is used to track the realised impact of this {t("generic.idea")}. It is only visible to
              project assignees and {t("generic.challenge")} managers.
            </div>
            {!impactStatements.finalOutcome ? (
              <Button
                secondary
                onClick={() => {
                  setCurrentImpactStage("finalOutcome")
                }}
              >
                <Icon name="plus" />
                Create outcome
              </Button>
            ) : (
              <>
                <div>
                  <Label>{Object.keys(impactStatements.finalOutcome.impacts).length} impacts</Label>
                </div>
                <div style={{ display: "flex", flex: 1 }}>
                  <Button
                    style={{ flex: 1 }}
                    onClick={() => {
                      setCurrentImpactStage("finalOutcome")
                    }}
                  >
                    <Icon name="pencil" />
                    Edit
                  </Button>
                  <Button
                    icon
                    basic
                    onClick={() => {
                      util
                        .confirm(
                          "Delete final outcome?",
                          "Are you sure you want to delete the final outcome statement? This actions is instant and irreversible.",
                        )
                        .then(() => {
                          deleteStatement(impactStatements.finalOutcome._id)
                        })
                        .catch(() => {})
                    }}
                  >
                    <Icon name="trash" />
                  </Button>
                </div>
              </>
            )}
          </div>
        </Segment>
      </SegmentGroup>
    </div>
  )
}

export default ImpactStatementManagement
